import { getLocalStorage, setLocalStorage } from "./localstorage";

export const CATALOG_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

const CACHE_PREFIX = "glossa:catalog:";

export interface CatalogCacheEntry<T> {
  data: T;
  fetchedAt: number;
}

const memoryCache = new Map<string, CatalogCacheEntry<unknown>>();

export function catalogCacheKey(scope: string, userId: string | number): string {
  return `${CACHE_PREFIX}${scope}:${userId}`;
}

export function isCatalogCacheFresh(
  fetchedAt: number,
  ttlMs = CATALOG_CACHE_TTL_MS,
): boolean {
  return Date.now() - fetchedAt < ttlMs;
}

export function readCatalogCache<T>(key: string): CatalogCacheEntry<T> | null {
  const fromMemory = memoryCache.get(key);
  if (fromMemory) {
    return fromMemory as CatalogCacheEntry<T>;
  }

  const fromStorage = getLocalStorage<CatalogCacheEntry<T>>(key);
  if (fromStorage?.data !== undefined && fromStorage.fetchedAt) {
    memoryCache.set(key, fromStorage as CatalogCacheEntry<unknown>);
    return fromStorage;
  }

  return null;
}

export function writeCatalogCache<T>(key: string, data: T): void {
  const entry: CatalogCacheEntry<T> = {
    data,
    fetchedAt: Date.now(),
  };
  memoryCache.set(key, entry as CatalogCacheEntry<unknown>);
  setLocalStorage(key, entry);
}

export function invalidateCatalogCache(key: string): void {
  memoryCache.delete(key);
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function getCatalogUserKey(): string | null {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const user = JSON.parse(raw) as { id?: string | number };
    if (user?.id == null) return null;
    return String(user.id);
  } catch {
    return null;
  }
}
